import { faEnvelopeOpenText } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Stack } from '@mantine/core';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { CampaignClass, type EOnboardingTaskType, REGEX_INPUT_VALIDATION } from '@feathr/blackbox';
import { Button, ButtonValid, Input, Modal, Pill, showError, Well } from '@feathr/components';
import ProjectSelect from '@feathr/extender/components/ProjectSelect';
import { useLocalUrl, useStore } from '@feathr/extender/state';
import { validate } from '@feathr/rachis';

interface ICreateEmailCampaignModalProps {
  onClose: () => void;
  onComplete?: (taskType: EOnboardingTaskType) => void;
}

function CreateEmailCampaignModal({
  onClose,
}: Readonly<ICreateEmailCampaignModalProps>): JSX.Element {
  const { Campaigns } = useStore();
  const history = useHistory();
  const localUrl = useLocalUrl();
  const { t } = useTranslation();
  const [selectedProjectId, setSelectedProjectId] = useState<string | undefined>(undefined);
  const [campaignName, setCampaignName] = useState('');

  function validateCampaignName(): string | undefined {
    const error: Array<string | undefined> = validate.single(campaignName, {
      presence: {
        allowEmpty: false,
        message: t("Campaign name can't be blank"),
      },
      format: {
        pattern: REGEX_INPUT_VALIDATION,
        flags: 'i',
        message: t('Campaign names must consist of letters, numbers, and basic symbols.'),
      },
    });
    return error ? error[0] : undefined;
  }

  // During this modal stage, the campaign object is not yet created, so we can't use its validate method.
  function validateCampaignProject(): string | undefined {
    if (selectedProjectId) {
      return;
    }
    const error: Array<string | undefined> = validate.single(selectedProjectId, {
      presence: {
        allowEmpty: false,
        message: t('^Please select a project.'),
      },
    });
    return error ? error[0] : undefined;
  }

  async function handleCreate(): Promise<void> {
    try {
      if (!selectedProjectId) {
        throw new Error(t('Please select a project.'));
      }
      const response = await Campaigns.addCampaign(CampaignClass.PinpointEmail, selectedProjectId, {
        name: campaignName,
      });

      history.push(`${localUrl(response.getItemUrl('edit'))}?onboarding=true`);
    } catch (error) {
      showError(error, t);
    } finally {
      onClose();
    }
  }

  const validationErrors = [validateCampaignName(), validateCampaignProject()].filter(
    Boolean,
  ) as string[];

  return (
    <Modal
      onClose={onClose}
      opened={true}
      rightActions={
        <>
          <Button onClick={onClose}>{t('Cancel')}</Button>
          <ButtonValid errors={validationErrors} onClick={handleCreate} type={'primary'}>
            {t('Create')}
          </ButtonValid>
        </>
      }
      title={t('Create a campaign')}
    >
      <Stack>
        <Well label={t('Campaign Type')} layout={'horizontal'}>
          <Pill prefix={<FontAwesomeIcon icon={faEnvelopeOpenText} />} theme={'yellow'}>
            {t('Single Send')}
          </Pill>
        </Well>

        <ProjectSelect onChangeSingle={setSelectedProjectId} value={selectedProjectId} />

        <Input
          label={t('Campaign name')}
          onChange={(newValue) => setCampaignName(newValue ?? '')}
          placeholder={t('Enter a campaign name')}
          type={'text'}
          value={campaignName}
        />
      </Stack>
    </Modal>
  );
}

export default CreateEmailCampaignModal;
